// Reusable inline-edit field for AssetDetail extension rows and (future) mass-edit tables.
// Click pencil → input/select/checkbox → blur or Enter saves → toast on error + revert.
// Optimistic UI: the local value updates immediately, the parent decides what to do if the
// server rejects (typically: revert by re-fetching the asset).

import React, { useState, useEffect, useRef } from 'react';
import { IconButton } from '../components/ui/Button';
import { Field, TextArea } from '../components/ui/Field';
import Select from '../components/ui/Select';
import { YesNo } from '../components/ui/YesNo';
import Tooltip from '../components/ui/Tooltip';
import Appear from '../components/ui/Appear';
import { IconPencil } from '../components/ui/icons';

type FieldType = 'text' | 'number' | 'select' | 'checkbox';

export interface InlineEditFieldProps {
  label?: string;                         // optional — omit in mass-edit tables
  name?: string;                          // accessible name when there is no visible label (a table cell)
  value: string | number | boolean | null;
  fieldType?: FieldType;                  // default 'text'
  options?: Array<{ value: string; label: string }>;  // required when fieldType='select'
  mono?: boolean;                         // monospace rendering (IPs, serials)
  readOnly?: boolean;                     // no pencil, no edit
  placeholder?: string;
  onSave: (newValue: string | number | boolean | null) => Promise<void>;
  // Optional renderer for the READ view — lets callers add icons, chips, etc.
  renderValue?: (v: string | number | boolean | null) => React.ReactNode;
  // Multiline editing: edit mode renders a <textarea>, Enter inserts newlines, blur
  // (or Cmd/Ctrl+Enter) commits, Escape cancels. Read view preserves newlines via
  // whitespace-pre-wrap. Used on AssetDetail's Notes field.
  multiline?: boolean;
  // List-view truncation: read view collapses to a single line with overflow ellipsis
  // and shows the full value as a title tooltip. Mutually exclusive with multiline.
  truncate?: boolean;
}

const InlineEditField: React.FC<InlineEditFieldProps> = ({
  label, name, value, fieldType = 'text', options, mono, readOnly,
  placeholder, onSave, renderValue, multiline, truncate,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]   = useState<string>(stringify(value));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // A select commits the moment an option is picked; closing its list without a pick cancels.
  const picked = useRef(false);
  const committing = useRef(false);

  // Keep draft in sync with prop changes that happen while we're NOT editing.
  useEffect(() => {
    if (!editing) setDraft(stringify(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement && fieldType === 'text') {
        inputRef.current.select();
      }
    }
  }, [editing, fieldType]);

  const commit = async (raw: string = draft) => {
    if (saving) return;
    const parsed = parseByType(raw, fieldType);
    const current = value;
    // No-op commit — just exit edit mode. Treat '' as null for text/number to match the
    // DB nullability convention.
    if (eq(parsed, current)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(parsed);
      setEditing(false);
    } catch {
      // Revert local draft to server value on failure. Parent shows toast.
      setDraft(stringify(value));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(stringify(value));
    setEditing(false);
  };

  // Checkboxes are click-to-toggle; no edit-mode state, no Enter/Escape. They save immediately.
  if (fieldType === 'checkbox') {
    const bool = value === true;
    const handleToggle = async () => {
      if (readOnly || saving) return;
      setSaving(true);
      try {
        await onSave(!bool);
      } catch {
        // Parent's onSave already toasted; we only need to keep the optimistic state in sync.
        // The caller's setAsset(updated) won't fire on rejection, so the chip continues to render
        // off the server-truth `value` — no manual local revert required.
      } finally {
        setSaving(false);
      }
    };
    return (
      <Row label={label}>
        <YesNo
          label={label ?? name ?? 'Value'}
          value={bool}
          onChange={handleToggle}
          busy={saving}
          readOnly={readOnly}
          size="sm"
        />
      </Row>
    );
  }

  // Read view: value + pencil to enter edit mode.
  if (!editing) {
    const displayValue = renderValue
      ? renderValue(value)
      : (value === null || value === undefined)
        ? <span className="italic text-on-surface-disabled">not set</span>
        // For select fields, render the human-readable label from options rather
        // than the stored value (so 'in_service' displays as 'In Service').
        : (fieldType === 'select' && options
            ? (options.find(o => o.value === String(value))?.label ?? String(value))
            : String(value));
    // truncate (list view): single-line, ellipsis, full text in tooltip.
    // multiline (detail view): preserve newlines.
    // default: inline string.
    const valueClass = truncate
      ? `text-white text-sm flex-1 truncate max-w-[200px] ${mono ? 'font-mono' : ''}`
      : multiline
        ? `text-white text-sm flex-1 whitespace-pre-wrap ${mono ? 'font-mono' : ''}`
        : `text-white text-sm flex-1 ${mono ? 'font-mono' : ''}`;
    return (
      <Row label={label}>
        <div className="flex items-center gap-2 group">
          <div
            className={valueClass}
            title={truncate && typeof value === 'string' ? value : undefined}
          >
            {displayValue}
          </div>
          {!readOnly && (
            <Tooltip label={`Edit ${label ?? 'field'}`}>
              <IconButton
                size="sm"
                onClick={() => setEditing(true)}
                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                label={`Edit ${label ?? 'field'}`}
              >
                <IconPencil />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Row>
    );
  }

  // Edit view — select / textarea / input based on fieldType + multiline.
  // Multiline auto-grows: rows = number of newlines in the draft, clamped to [3, 10].
  const multilineRows = multiline
    ? Math.min(10, Math.max(3, draft.split('\n').length))
    : undefined;
  return (
    <Row label={label}>
      <div className="flex items-start gap-2">
        {fieldType === 'select' ? (
          <div className="min-w-0 flex-1">
            <Select
              label={label ?? 'Value'}
              size="sm"
              defaultOpen
              value={draft}
              options={[{ key: '', label: 'None' }, ...(options ?? []).map((o) => ({ key: o.value, label: o.label }))]}
              onChange={(key) => {
                picked.current = true;
                committing.current = true;
                setDraft(key);
                commit(key).finally(() => { committing.current = false; });
              }}
              onClose={() => { if (!picked.current) cancel(); picked.current = false; }}
              onBlur={() => { if (!committing.current) cancel(); }}
              disabled={saving}
            />
          </div>
        ) : multiline ? (
          <TextArea
            ref={(el) => { inputRef.current = el; }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit()}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter commits. Plain Enter inserts a newline (default behavior).
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
              if (e.key === 'Escape') cancel();
            }}
            placeholder={placeholder}
            disabled={saving}
            rows={multilineRows}
            className={`flex-1 ${mono ? 'font-mono' : ''}`}
          />
        ) : (
          <Field
            ref={(el) => { inputRef.current = el; }}
            type={fieldType}
            size="sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit()}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  commit();
              if (e.key === 'Escape') cancel();
            }}
            placeholder={placeholder}
            disabled={saving}
            className={`flex-1 ${mono ? 'font-mono' : ''}`}
          />
        )}
        <Appear show={saving} className="text-xs text-on-surface-variant">saving…</Appear>
      </div>
    </Row>
  );
};

const Row: React.FC<{ label?: string; children: React.ReactNode }> = ({ label, children }) => {
  if (!label) return <>{children}</>;
  return (
    <li>
      <div className="text-on-surface-variant text-xs uppercase tracking-wide mb-1">{label}</div>
      {children}
    </li>
  );
};

function stringify(v: string | number | boolean | null): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

function parseByType(raw: string, t: FieldType): string | number | boolean | null {
  if (t === 'checkbox') return raw === 'true';
  if (t === 'number') {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    // NaN guard: typing "abc" into a number field yielded Number('abc') = NaN, which
    // would have been written to the DB as a SQL NaN. Reject by returning null.
    return Number.isFinite(n) ? n : null;
  }
  // text, select: empty string maps to null so the DB column can be cleared.
  return raw.trim() === '' ? null : raw;
}

function eq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  return false;
}

export default InlineEditField;
